import { z } from "zod/v4";

import { valueof } from "api/common/utils/type.utils";
import {
  ECR_FILE_TYPES,
  ECR_PROCESS_LOCATIONS,
  ECR_STATUSES,
  ECR_TYPES,
} from "api/memberPortal/ecr/ecrOptions.types";
import { FILE_VISIBILITY_TYPES } from "api/memberPortal/files/files.types";
import {
  createZodNumberOption,
  createZodEnums,
} from "components/Forms/utils/validation.utils";

export interface EcrFile {
  fileType: string;
  fileLabel: string;
  documentVisibility: string;
  file: File;
}

export const ECR_UPLOAD_TYPES = {
  EAR_REVIEW: "EAR Review",
  SECURITY_TARGET: "Security Target",
  PRODUCT_INFORMATION: "Product Information",
} as const;
export type EcrUploadType = valueof<typeof ECR_UPLOAD_TYPES>;

const EcrFileSchema = z.object({
  fileType: createZodEnums({ valueObject: ECR_UPLOAD_TYPES }),
  fileLabel: z.string(),
  documentVisibility: createZodEnums({ valueObject: FILE_VISIBILITY_TYPES }),
  file: z.file(),
});

export const EcrFormSchema = z.object({
  product: createZodNumberOption("Product is required"),

  title: z.string().optional(),
  process_location: createZodEnums({
    valueObject: ECR_PROCESS_LOCATIONS,
    customErrorText: "Process location is required",
  }),
  type: createZodEnums({
    valueObject: ECR_TYPES,
    customErrorText: "Comment type is required",
  }),
  status: createZodEnums({
    valueObject: ECR_STATUSES,
    customErrorText: "Comment status is required",
  }),
  documentTypes: z
    .array(createZodEnums({ valueObject: ECR_FILE_TYPES }))
    .min(1, "At least one document type is required"),

  cite: z.string().min(1, "Citation is required"),

  // These fields are not part of the final submission
  // They are only used to populate the `files` array
  fileType: createZodEnums({
    valueObject: ECR_UPLOAD_TYPES,
    allowEmptyString: true,
  }).optional(),
  documentVisibility: createZodEnums({
    valueObject: FILE_VISIBILITY_TYPES,
    allowEmptyString: true,
  }).optional(),
  fileLabel: z.string().optional(),
  // This is the only thing that is actually submitted
  files: z.array(EcrFileSchema),

  comment: z.string().min(1, "Comment is required"),
  rationale: z.string().optional(),

  protectionProfile: createZodNumberOption().optional(),
  technicalDecisions: z.array(createZodNumberOption()),
  requirement: z.string().optional(),
});

// Creating a partial schema to allow for empty default values
// I'm not sure if this is the best way to handle this but it does validate properly
export const EcrFormInputSchema = EcrFormSchema.partial({
  product: true,
});

export type EcrForm = z.infer<typeof EcrFormSchema>;
export type EcrFormInput = z.infer<typeof EcrFormInputSchema>;
